import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApexOptions } from 'apexcharts'
import { KTIcon } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { BOOKABLE_CATEGORIES } from '../../../reusable'
import { ALL_CATEGORY_OPTION } from './useBookingAnalytics'
import { ToastContainer } from 'react-toastify'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import DataTable from 'react-data-table-component'
import Select from 'react-select'

import KpiCard from './components/KpiCard'
import ChartCard from './components/ChartCard'
import { selectStyles } from './components/selectStyles'
import { useSatisfactionRatings, type SatisfactionSummary, type DistributionItem, type ListingRating } from './useSatisfactionRatings'

// ─── Star Rating Display ───
const StarRating: FC<{ rating: number; size?: string }> = React.memo(({ rating, size = 'fs-7' }) => {
  const full = Math.floor(rating ?? 0)
  return (
    <div className="d-flex align-items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <KTIcon
          key={i}
          iconName="star"
          className={`${size} ${i < full ? 'text-warning' : 'text-gray-300'}`}
        />
      ))}
      <span className={`fw-bold text-gray-900 ${size} ms-1`}>{(rating ?? 0).toFixed(1)}</span>
    </div>
  )
})

// ─── Rating Progress Bar ───
const RatingBar: FC<{ star: number; count: number; total: number }> = React.memo(({ star, count, total }) => {
  const pct = (total ?? 0) > 0 ? ((count ?? 0) / total) * 100 : 0
  const colors: Record<number, string> = { 5: '#50cd89', 4: '#7239ea', 3: '#ffc700', 2: '#f1416c', 1: '#f1416c' }
  return (
    <div className="d-flex align-items-center gap-3 mb-3">
      <div className="d-flex align-items-center gap-1" style={{ minWidth: 50 }}>
        <span className="fw-bold text-gray-800 fs-7">{star}</span>
        <KTIcon iconName="star" className="fs-7 text-warning" />
      </div>
      <div className="flex-grow-1 bg-light rounded" style={{ height: 10 }}>
        <div className="rounded" style={{ width: `${pct}%`, height: '100%', backgroundColor: colors[star] ?? '#ccc', transition: 'width 0.5s ease' }}></div>
      </div>
      <span className="fw-semibold text-gray-600 fs-8" style={{ minWidth: 40 }}>{count ?? 0}</span>
      <span className="text-gray-400 fs-9" style={{ minWidth: 40 }}>{pct.toFixed(0)}%</span>
    </div>
  )
})

// ─── Filter Bar ───
const FilterBar = React.memo<{
  dateRange: Date[]
  selectedCategory: any
  onDateChange: (dates: Date[]) => void
  onCategoryChange: (val: any) => void
  onClear: () => void
  t: (key: string) => string
}>(({ dateRange, selectedCategory, onDateChange, onCategoryChange, onClear, t }) => {
  const hasFilters = (dateRange?.length ?? 0) > 0 || (selectedCategory?.value && selectedCategory.value !== '')
  const flatpickrRef = useRef<any>(null)

  const clearDate = useCallback(() => {
    if (flatpickrRef.current?.flatpickr) flatpickrRef.current.flatpickr.clear()
    onDateChange([])
  }, [onDateChange])

  return (
    <div className="card border-0 shadow-sm mb-6">
      <div className="card-body py-5 px-6">
        <div className="d-flex flex-wrap align-items-center gap-4">
          <div className="d-flex align-items-center position-relative">
            <KTIcon iconName="calendar" className="fs-3 position-absolute ms-4 text-gray-500" />
            <Flatpickr
              ref={flatpickrRef}
              className="form-control form-control-solid w-275px ps-12 fs-7"
              placeholder={t('Select date range')}
              value={dateRange}
              onChange={(dates: Date[]) => { if (dates?.length === 2) onDateChange(dates) }}
              options={{ mode: 'range' as const, dateFormat: 'd M Y', monthSelectorType: 'static' as const }}
            />
          </div>
          {(dateRange?.length ?? 0) > 0 ? (
            <button className="btn btn-icon btn-sm btn-light-danger" onClick={clearDate} title={t('Clear date')}>
              <KTIcon iconName="cross" className="fs-5" />
            </button>
          ) : null}
          <div style={{ minWidth: 200 }}>
            <Select
              options={[ALL_CATEGORY_OPTION, {label: "Cafe & Restaurant", value: "3"}, ...BOOKABLE_CATEGORIES]}
              value={selectedCategory}
              onChange={(val: any) => onCategoryChange(val ?? ALL_CATEGORY_OPTION)}
              isClearable={selectedCategory?.value !== ''}
              placeholder={t('All')}
              styles={selectStyles}
            />
          </div>
          {hasFilters ? (
            <button className="btn btn-sm btn-light-danger fs-7" onClick={onClear}>
              <KTIcon iconName="cross" className="fs-5 me-1" />
              {t('Clear All')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
})

// ─── Summary Section ───
const SummarySection: FC<{ summary: SatisfactionSummary; t: (key: string) => string }> = React.memo(({ summary, t }) => (
  <div className="row g-4 mb-6">
    <KpiCard title={t('Overall Rating')} value={`${Number(summary?.overallRating ?? 0).toFixed(1)} / 5`} color="warning" icon="star" />
    <KpiCard title={t('Total Reviews')} value={(summary?.totalReviews ?? 0).toLocaleString()} color="primary" icon="message-text-2" />
    <KpiCard title={t('Satisfied')} value={(summary?.satisfied?.count ?? 0).toLocaleString()} subtitle={summary?.satisfied?.percent ? `${summary.satisfied.percent}%` : ''} color="success" icon="like" />
    <KpiCard title={t('Unsatisfied')} value={(summary?.unsatisfied?.count ?? 0).toLocaleString()} subtitle={summary?.unsatisfied?.percent ? `${summary.unsatisfied.percent}%` : ''} color="danger" icon="dislike" />
  </div>
))

// ─── Distribution Section ───
const DistributionSection: FC<{ summary: SatisfactionSummary; distribution: DistributionItem[]; t: (key: string) => string }> = React.memo(({ summary, distribution, t }) => (
  <div className="card border-0 shadow-sm h-100">
    <div className="card-header border-0 pt-6 pb-0">
      <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Rating Distribution')}</h3>
    </div>
    <div className="card-body pt-5">
      <div className="d-flex align-items-center gap-3 mb-6">
        <span className="fw-bolder text-gray-900 fs-2x">{Number(summary?.overallRating ?? 0).toFixed(1)}</span>
        <div>
          <StarRating rating={summary?.overallRating ?? 0} size="fs-4" />
          <div className="text-gray-500 fs-8 mt-1">{summary?.totalReviews ?? 0} {t('reviews')}</div>
        </div>
      </div>
      {[5, 4, 3, 2, 1].map((star) => {
        const d = (distribution ?? []).find((item: any) => Number(item?.star) === star)
        return <RatingBar key={star} star={star} count={d?.count ?? 0} total={summary?.totalReviews ?? 1} />
      })}
    </div>
  </div>
))

// ─── Ratings by Listing Table ───
const RatingsTable: FC<{ data: ListingRating[]; isLoading: boolean; t: (key: string) => string }> = React.memo(({ data, isLoading, t }) => {
  const columns = useMemo(() => [
    {
      name: t('Listing'),
      cell: (row: any) => (
        <div className="py-2">
          <div className="fw-bold text-gray-900 fs-6">{row?.listingName ?? '-'}</div>
          <span className="badge badge-light-primary fs-9">{row?.listingType ?? ''}</span>
        </div>
      ),
      grow: 2,
    },
    {
      name: t('Rating'),
      cell: (row: any) => <StarRating rating={row?.rating ?? 0} size="fs-6" />,
      width: '170px',
    },
    {
      name: t('Reviews'),
      cell: (row: any) => <span className="fw-bold text-gray-900 fs-6">{row?.reviews ?? 0}</span>,
      width: '100px',
    },
    {
      name: t('Distribution'),
      cell: (row: any) => {
        const dist = row?.distribution ?? {}
        const total = row?.reviews ?? 1
        const bars = [
          { star: 5, pct: ((dist?.['5'] ?? 0) / total) * 100, color: '#50cd89' },
          { star: 4, pct: ((dist?.['4'] ?? 0) / total) * 100, color: '#7239ea' },
          { star: 3, pct: ((dist?.['3'] ?? 0) / total) * 100, color: '#ffc700' },
          { star: 2, pct: ((dist?.['2'] ?? 0) / total) * 100, color: '#f1416c' },
          { star: 1, pct: ((dist?.['1'] ?? 0) / total) * 100, color: '#f1416c' },
        ]
        return (
          <div className="d-flex align-items-center gap-1 py-2" style={{ width: 150 }}>
            {bars.map(b => (
              <div key={b.star} className="rounded" style={{ height: 8, flex: b.pct, backgroundColor: b.color, minWidth: b.pct > 0 ? 4 : 0 }}></div>
            ))}
          </div>
        )
      },
      width: '180px',
    },
    {
      name: t('Satisfaction'),
      cell: (row: any) => {
        const pct = row?.satisfiedPercent ?? 0
        const color = pct >= 70 ? 'success' : pct >= 50 ? 'warning' : 'danger'
        return <span className={`badge badge-light-${color} fw-bold fs-8 px-3 py-2`}>{pct}%</span>
      },
      width: '120px',
    },
  ], [t])

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Ratings by Listing')}</h3>
      </div>
      <div className="card-body pt-0">
        <DataTable
          columns={columns}
          data={data ?? []}
          progressPending={isLoading}
          progressComponent={
            <div className="py-10 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('Loading...')}</span>
              </div>
            </div>
          }
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 20, 30]}
          highlightOnHover
          noDataComponent={
            <div className="text-center py-10">
              <KTIcon iconName="star" className="fs-2tx text-gray-300 mb-5" />
              <div className="text-gray-500 fs-5">{t('No ratings found')}</div>
            </div>
          }
          paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        />
      </div>
    </div>
  )
})

// ─── Main Page ───
const SatisfactionRatings: FC = () => {
  const tFn = Tr()
  const tRef = useRef(tFn)
  tRef.current = tFn
  const t = useCallback((key: string) => tRef.current(key), [])

  const [dateRange, setDateRange] = useState<Date[]>([])
  const [selectedCategory, setSelectedCategory] = useState<any>(ALL_CATEGORY_OPTION)

  const { summary, distribution, trend, reviewsVolume, byListing, isLoading } = useSatisfactionRatings(selectedCategory, dateRange)

  useEffect(() => {
    document.title = 'Insayr | Satisfaction Ratings'
  }, [])

  // ─── Chart Options (memoized) ───
  const trendChartOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit' },
    series: [
      { name: t('Avg Rating'), data: (trend ?? []).map((item: any) => item?.avgRating ?? 0) },
    ],
    xaxis: { categories: (trend ?? []).map((item: any) => item?.label ?? item?.date ?? '') },
    yaxis: { min: 1, max: 5, tickAmount: 4 },
    colors: ['#ffc700'],
    stroke: { curve: 'smooth', width: 3 },
    markers: { size: 5, colors: ['#ffc700'], strokeWidth: 0 },
    dataLabels: { enabled: true, formatter: (val: number) => val?.toFixed?.(1) ?? '0', offsetY: -8 },
    grid: { borderColor: '#f1f3f8', strokeDashArray: 4 },
    tooltip: { theme: 'light' },
  }), [trend])

  const reviewsVolumeOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    series: [{ name: t('Reviews'), data: (reviewsVolume ?? []).map((item: any) => item?.reviews ?? 0) }],
    xaxis: { categories: (reviewsVolume ?? []).map((item: any) => item?.label ?? item?.date ?? '') },
    colors: ['#009ef7'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    yaxis: { labels: { formatter: (val: number) => Math.round(val).toString() } },
    grid: { borderColor: '#f1f3f8', strokeDashArray: 4 },
    tooltip: { theme: 'light', y: { formatter: (val: number) => Math.round(val).toString() } },
  }), [reviewsVolume])

  const handleDateChange = useCallback((dates: Date[]) => setDateRange(dates?.length === 2 ? dates : []), [])
  const handleCategoryChange = useCallback((val: any) => setSelectedCategory(val), [])
  const handleClear = useCallback(() => {
    setDateRange([])
    setSelectedCategory(ALL_CATEGORY_OPTION)
  }, [])

  return (
    <>
      <ToastContainer />

      <FilterBar
        dateRange={dateRange}
        selectedCategory={selectedCategory}
        onDateChange={handleDateChange}
        onCategoryChange={handleCategoryChange}
        onClear={handleClear}
        t={t}
      />

      <SummarySection summary={summary} t={t} />

      {(summary?.totalReviews ?? 0) > 0 ? (
        <>
          {/* Distribution + Trend */}
          <div className="row g-4 mb-6">
            {(distribution?.length ?? 0) > 0 ? (
              <div className="col-lg-5">
                <DistributionSection summary={summary} distribution={distribution} t={t} />
              </div>
            ) : null}
            {(trend?.length ?? 0) > 0 ? (
              <div className={(distribution?.length ?? 0) > 0 ? 'col-lg-7' : 'col-12'}>
                <ChartCard title={t('Rating Trend')} chartId="satisfaction-trend-chart" options={trendChartOptions} height={280} />
              </div>
            ) : null}
          </div>

          {/* Reviews Volume */}
          {(reviewsVolume?.length ?? 0) > 0 ? (
            <div className="mb-6">
              <ChartCard title={t('Reviews Volume')} chartId="reviews-volume-chart" options={reviewsVolumeOptions} height={250} />
            </div>
          ) : null}
        </>
      ) : !isLoading ? (
        <div className="card border-0 shadow-sm mb-6">
          <div className="card-body text-center py-15">
            <KTIcon iconName="star" className="fs-3tx text-gray-300 mb-4" />
            <div className="text-gray-500 fs-5 fw-semibold">{t('No review data available')}</div>
            <div className="text-gray-400 fs-7 mt-2">{t('Try changing the filters or date range')}</div>
          </div>
        </div>
      ) : null}

      <RatingsTable data={byListing} isLoading={isLoading} t={t} />
    </>
  )
}

export default SatisfactionRatings
